import { z } from "zod/v4";

import { OrganizationDtoSchema } from "../../dtos/organizations";
import { UrlMatch, UrlMatchList } from "../../dtos/url-match";

export const ConversionStageReachedList = [
  "started",
  "contact_info_submitted",
  "address_info_submitted",
  "checkout_block_viewed",
  "onsite_upsell_viewed",
  "post_purchase_offer_viewed",
  "any",
  "added_to_cart",
] as const;

export type ConversionStageReachedType =
  (typeof ConversionStageReachedList)[number];

// The checkout-funnel stages that are actually valid for experience-level
// analytics. Unlike the broader ConversionStageReachedList (which the sitewide
// UI/store reuses for non-checkout stages like "added_to_cart" and the "viewed"
// events), only these three map to a real value in the analytics-v2 experience
// pipe (see analytics-v2 reached_checkout_stage Literal + tinybird.py 1/2/3
// mapping). Used to validate the external experience analytics filter.
export const CheckoutStageReachedList = [
  "started",
  "contact_info_submitted",
  "address_info_submitted",
] as const;

export type CheckoutStageReachedType =
  (typeof CheckoutStageReachedList)[number];

export const DeviceTypeList = ["any", "mobile", "desktop"] as const;
export type DeviceType = (typeof DeviceTypeList)[number];

export const SitewideSnapshotsTimeFrameList = [
  "today",
  "yesterday",
  "last_7_days",
  "last_30_days",
  "last_90_days",
  "last_365_days",
] as const;

export type SitewideSnapshotsTimeFrame =
  (typeof SitewideSnapshotsTimeFrameList)[number];

export const SitewideSnapshotsComparisonTimeFrameList = [
  "previous_period",
  "same_day_previous_month",
  "same_day_previous_week",
  "same_day_previous_year",
  "same_week_previous_month",
  "same_week_previous_year",
  "same_month_previous_year",
  "same_months_previous_year",
] as const;

export type SitewideSnapshotsComparisonTimeFrame =
  (typeof SitewideSnapshotsComparisonTimeFrameList)[number];

export const VisitorTypeSchema = ["any", "new", "returning"] as const;
export type VisitorType = (typeof VisitorTypeSchema)[number];

export const PurchaseLikelihoodList = [
  "any",
  "lower",
  "medium",
  "higher",
] as const;

export type PurchaseLikelihoodType = (typeof PurchaseLikelihoodList)[number];
export const CustomerTypeList = ["new", "returning"] as const;
export type CustomerType = (typeof CustomerTypeList)[number];

export const AnalyzeContext = z.object({
  org: OrganizationDtoSchema.pick({ id: true, shopId: true, timezone: true }),
  latestRunTs: z.string(),
  cacheInterval: z.number(),
});

export type AnalyzeContextType = z.infer<typeof AnalyzeContext>;

export const ChannelTypeSchema = [
  "Referral",
  "All Social",
  "Organic Social",
  "Paid Social",
  "Organic Search",
  "Paid Search",
  "Paid Shopping",
  "Email",
  "Google",
  "Facebook",
  "Instagram",
  "Linktree",
  "TikTok",
  "Twitter",
  "Direct",
  "SMS",
  "Other",
  "Meta",
  "Link Tree",
  "Wunderkind Text",
  "Youtube",
  "Affiliate",
  "any",
] as const;

export type ChannelType = (typeof ChannelTypeSchema)[number];

// Traffic-source channels the analytics service actually buckets on. This is
// deliberately *not* `ChannelTypeSchema`: that list is the audience-targeting
// vocabulary (evaluated client-side by the plugin), and six of its values —
// `All Social`, `Linktree`, `Link Tree`, `Meta`, `Wunderkind Text` and the
// `any` sentinel — have no analytics bucket, so accepting them on an analytics
// filter meant a documented value 400'd downstream in analytics-v2. It also
// omitted eight channels analytics does bucket on (`Klaviyo`, `Attentive`,
// `Postscript`, `Paid Video`, `Display`, `Paid Other`, `Organic Shopping`,
// `Organic Video`). Keep this list in sync with analytics-v2's channel enum.
export const AnalyticsChannelList = [
  "Instagram",
  "Facebook",
  "Google",
  "Twitter",
  "TikTok",
  "Youtube",
  "Klaviyo",
  "Attentive",
  "Postscript",
  "Paid Shopping",
  "Paid Search",
  "Paid Social",
  "Paid Video",
  "Display",
  "Paid Other",
  "Organic Shopping",
  "Organic Social",
  "Organic Video",
  "Organic Search",
  "Other",
  "Referral",
  "Affiliate",
  "SMS",
  "Email",
  "Direct",
] as const;

export type AnalyticsChannelType = (typeof AnalyticsChannelList)[number];

// Narrows an arbitrary channel string to one analytics can filter on. Use this
// when feeding a list that is typed off the audience vocabulary (e.g. the
// dashboard's shared channel picker options) into an analytics filter, so
// audience-only values are dropped rather than forwarded to a 400.
export const isAnalyticsChannel = (
  value: string
): value is AnalyticsChannelType =>
  (AnalyticsChannelList as readonly string[]).includes(value);

// Match operators the Tinybird analytics pipes actually implement. This is
// UrlMatchList minus `regex`: audiences, page targeting and custom events
// evaluate regex client-side in JS, but no analytics pipe has a regex branch, so
// accepting it on an analytics filter meant a documented value 400'd downstream
// in analytics-v2 (whose FilterType lists these same ten). Keep the two in sync.
export const AnalyticsMatchList = [
  "equals",
  "contains",
  "startsWith",
  "endsWith",
  "doesNotEqual",
  "doesNotContain",
  "doesNotStartWith",
  "doesNotEndWith",
  "isNull",
  "isNotNull",
] as const satisfies readonly UrlMatch[];

export type AnalyticsMatchType = (typeof AnalyticsMatchList)[number];

/** @deprecated Use `AnalyticsMatchList` — identical values, accurate scope. */
export const LandingPageFilterList = AnalyticsMatchList;

/** @deprecated Use `AnalyticsMatchType` — identical values, accurate scope. */
export type LandingPageFilterType = AnalyticsMatchType;

/** Operators that match on the *absence* of a value and therefore take none. */
export const NULL_MATCH_OPERATORS = ["isNull", "isNotNull"] as const;

export const isNullMatchOperator = (
  operator: AnalyticsMatchType
): operator is (typeof NULL_MATCH_OPERATORS)[number] =>
  (NULL_MATCH_OPERATORS as readonly string[]).includes(operator);

/**
 * Every analytics match filter has the same operator/value contract, and every
 * violation of it used to fail *silently*: the Tinybird pipes guard each filter
 * block on the value parameter being defined, and both the node-backend payload
 * cleaner and analytics-v2 strip nulls and empty strings before the query is
 * built. So an operator sent without its value produced a 200 with the filter
 * simply not applied — unfiltered numbers, no error. These refinements move that
 * failure to the edge, as a documented validation error naming the field.
 *
 * A value with no operator is rejected for the same reason, in the other
 * direction. Both mirror the dashboard's existing drawer validation
 * (`analyticsFilters/utils.ts`), which already blocks these combinations in the
 * UI — so this makes the schema agree with it rather than introducing a new rule.
 */
const nonEmpty = (value: string | null | undefined): boolean =>
  typeof value === "string" && value.trim().length > 0;

export const UrlParamPayloadSchema = z
  .object({
    queryParam: z.string().nullable(),
    queryParamValue: z.string().nullable(),
    queryParamFilterType: z.enum(AnalyticsMatchList).nullable(),
  })
  .superRefine((urlParam, ctx) => {
    const { queryParam, queryParamValue, queryParamFilterType } = urlParam;

    // All-empty means "no query-param filter" — the dashboard sends this shape by
    // default, so it must stay valid.
    if (
      !nonEmpty(queryParam) &&
      !nonEmpty(queryParamValue) &&
      queryParamFilterType === null
    ) {
      return;
    }

    if (queryParamFilterType === null) {
      ctx.addIssue({
        code: "custom",
        path: ["queryParamFilterType"],
        message:
          "queryParamFilterType is required when any other urlParam field is set, otherwise the filter is ignored.",
      });
      return;
    }

    // The pipe indexes query_params by name, so without it nothing can be matched.
    if (!nonEmpty(queryParam)) {
      ctx.addIssue({
        code: "custom",
        path: ["queryParam"],
        message:
          "queryParam (the parameter name) is required whenever queryParamFilterType is set.",
      });
    }

    if (isNullMatchOperator(queryParamFilterType)) {
      if (nonEmpty(queryParamValue)) {
        ctx.addIssue({
          code: "custom",
          path: ["queryParamValue"],
          message: `queryParamFilterType '${queryParamFilterType}' matches on the presence or absence of the parameter and takes no value; set queryParamValue to null.`,
        });
      }
      return;
    }

    if (!nonEmpty(queryParamValue)) {
      ctx.addIssue({
        code: "custom",
        path: ["queryParamValue"],
        message: `queryParamFilterType '${queryParamFilterType}' requires a non-empty queryParamValue. Use 'isNull' to match sessions where the parameter is absent.`,
      });
    }
  });

export const CountryCodesFilterList = ["include", "exclude"] as const;

export type CountryCodesFilterType = (typeof CountryCodesFilterList)[number];

export const CurrencyCodesFilterList = ["include", "exclude"] as const;

export type CurrencyCodesFilterType = (typeof CurrencyCodesFilterList)[number];

export const LandingPagePayloadSchema = z
  .object({
    landingPageUrlPath: z.string().nullable(),
    landingPageUrlPathFilterType: z.enum(AnalyticsMatchList).nullable(),
  })
  .superRefine((landingPage, ctx) => {
    const { landingPageUrlPath, landingPageUrlPathFilterType } = landingPage;

    if (
      !nonEmpty(landingPageUrlPath) &&
      landingPageUrlPathFilterType === null
    ) {
      return;
    }

    if (landingPageUrlPathFilterType === null) {
      ctx.addIssue({
        code: "custom",
        path: ["landingPageUrlPathFilterType"],
        message:
          "landingPageUrlPathFilterType is required when landingPageUrlPath is set, otherwise the filter is ignored.",
      });
      return;
    }

    if (isNullMatchOperator(landingPageUrlPathFilterType)) {
      if (nonEmpty(landingPageUrlPath)) {
        ctx.addIssue({
          code: "custom",
          path: ["landingPageUrlPath"],
          message: `landingPageUrlPathFilterType '${landingPageUrlPathFilterType}' matches on the absence or presence of a landing page and takes no path; set landingPageUrlPath to null.`,
        });
      }
      return;
    }

    if (!nonEmpty(landingPageUrlPath)) {
      ctx.addIssue({
        code: "custom",
        path: ["landingPageUrlPath"],
        message: `landingPageUrlPathFilterType '${landingPageUrlPathFilterType}' requires a non-empty landingPageUrlPath. Use 'isNull' to match sessions with no landing page.`,
      });
    }
  });

export const LandingPageFilterItemSchema = z
  .object({
    values: z.string().array().optional(),
    filterType: z.enum(AnalyticsMatchList),
  })
  .superRefine((item, ctx) => {
    const provided = (item.values ?? []).filter(nonEmpty);

    if (isNullMatchOperator(item.filterType)) {
      // An empty array is tolerated — callers whose serializer always emits the
      // key shouldn't be punished. Real values are contradictory and rejected.
      if (provided.length > 0) {
        ctx.addIssue({
          code: "custom",
          path: ["values"],
          message: `filterType '${item.filterType}' matches on the absence or presence of a landing page and takes no values; omit 'values'.`,
        });
      }
      return;
    }

    if (provided.length === 0) {
      ctx.addIssue({
        code: "custom",
        path: ["values"],
        message: `filterType '${item.filterType}' requires at least one non-empty entry in 'values'. Use 'isNull' to match sessions with no landing page.`,
      });
    }
  });

export type LandingPageFilterItemType = z.infer<
  typeof LandingPageFilterItemSchema
>;

export const LandingPageFiltersOperatorList = ["or", "and"] as const;

export type LandingPageFiltersOperatorType =
  (typeof LandingPageFiltersOperatorList)[number];

export const LandingPageTypeList = [
  "PDP",
  "Collection",
  "Content",
  "Blog",
  "Homepage",
  "Search",
  "Cart",
  "Other",
] as const;

export type LandingPageType = (typeof LandingPageTypeList)[number];

export type UrlParamPayloadType = z.infer<typeof UrlParamPayloadSchema>;

export const DatasetIdList = [
  "order_line_items",
  "orders",
  "aiProfit",
  "variation_overview",
  "variation_audience",
  "variation_timeseries_discrete",
  "variation_timeseries_cumulative",
  "experience_countries",
  "variation_shipping_method",
  "variation_histogram",
  "variation_products",
  "variation_product_groups",
  "variation_order_unit_mix",
  "custom_events_plugin",
  "custom_events_server",
  "order_reconciliation",
  "order_reconciliation_export",
  "experience_session_order",
  "timeseries",
  "country_codes",
  "snapshots",
  "experience_shipping_methods",
  "timeseries_audience",
  "snapshots_audience",
  "holiday_benchmarking_single_day",
  "holiday_benchmarking_multiple_days",
  "order_distribution_histogram",
  "order_distribution_unit_mix",
  "conversion_snapshots",
  "conversion_funnel",
  "conversion_timeseries",
  "variation_post_metrics",
  "variation_post_metrics_audience",
] as const;

export type DatasetId = (typeof DatasetIdList)[number];

export type CastedTimes = {
  startTime: string | number | undefined | null;
  endTime: string | number | undefined | null;
};

export type CastedTimesTinybird = {
  startTs: string | number | undefined | null;
  endTs: string | number | undefined | null;
};

export const AnalyticsViewTypeList = [
  "test_orders_only",
  "sitewide_orders",
  "none",
] as const;

export type AnalyticsViewType = (typeof AnalyticsViewTypeList)[number];

export const AnalyticsViewTypeFeList = [
  ...AnalyticsViewTypeList,
  "specific_products",
] as const;

export type AnalyticsViewTypeFe = (typeof AnalyticsViewTypeFeList)[number];

export const UserBehaviorFilterSchema = z.object({
  viewedCheckout: z.boolean().nullish(),
  addedToCart: z.boolean().nullish(),
  viewedProductPage: z.boolean().nullish().optional(),
  viewedCollectionPage: z.boolean().nullish().optional(),
});

export type UserBehaviorFilterType = z.infer<typeof UserBehaviorFilterSchema>;

export const TimeseriesIntervalList = ["HOUR", "DAY", "WEEK"] as const;
export type TimeseriesInterval = (typeof TimeseriesIntervalList)[number];

export const HistogramMetricList = [
  "net_revenue",
  "net_product_revenue",
  "total_discount",
  "unit_quantity",
] as const;

export type HistogramMetricType = (typeof HistogramMetricList)[number];

export const HistogramKindList = ["histogram", "kde", "kde_cdf"] as const;
export type HistogramKindType = (typeof HistogramKindList)[number];

export const CustomEventsFilterTypeList = ["any", "all", "none"] as const;

export type CustomEventsFilterType =
  (typeof CustomEventsFilterTypeList)[number];

export const AnalyticsResourceTypeV2List = [
  "sitewide",
  "exports",
  "experiences",
  "customEvents",
  "summaryMetrics",
] as const;

export type AnalyticsResourceV2Type =
  (typeof AnalyticsResourceTypeV2List)[number];

export const RequestSourceList = [
  "internal",
  "external",
  "mcp",
  "shared_link",
] as const;

export type RequestSourceType = (typeof RequestSourceList)[number];

export const AnalyticsSelectedProductSchema = z.object({
  id: z.string(),
  title: z.string().nullable(),
  handle: z.string(),
});

export type AnalyticsSelectedProductType = z.infer<
  typeof AnalyticsSelectedProductSchema
>;

export const AnalyticsAudienceTypeList = [
  "country_code",
  "device_type",
  "visitor_type",
  "source_channel",
  "source_site",
  "landing_page_full_path",
  "purchase_likelihood",
] as const;

export type AnalyticsAudienceType = (typeof AnalyticsAudienceTypeList)[number];

// The `audience` breakdown dimensions sitewide analytics supports.
// Distinct from AnalyticsAudienceTypeList
export const ExternalSitewideAnalyticsAudienceList = [
  "device_type",
  "visitor_type",
  "source_channel",
  "source_site",
  "country_code",
  "landing_page_full_path",
] as const;

export const ExternalSitewideAnalyticsAudienceSchema = z.enum(
  ExternalSitewideAnalyticsAudienceList
);

export type ExternalSiteWideAnalyticsAudience =
  (typeof ExternalSitewideAnalyticsAudienceList)[number];

// Entry-point pivot values supported by the conversion funnel. Named
// `entryPoint` on the public contract (distinct from the `audience` param on
// the snapshot/timeseries endpoints) because on the funnel it pivots the
// step-1 entry-point nodes rather than segmenting KPIs — the shared word
// "audience" across endpoints was a source of caller confusion. Intentionally
// narrower than AnalyticsAudienceTypeList/ExternalSitewideAnalyticsAudienceList
// (which also include `country_code`, `landing_page_full_path`, and
// `purchase_likelihood`) — those dimensions are excluded from the funnel by
// design, because it doesn't surface them meaningfully.
//
// This list is the single definition of that set. The external API + MCP
// validate `entryPoint` against it, and the dashboard's Customer Journey
// section (`web/frontend/src/features/analytics/sitewideAnalytics/CustomerJourneySection.tsx`)
// derives its `?audience=` validation, its default entry point and its
// dropdown options from it rather than from a hand-maintained copy. So adding a
// member here does not only widen the API surface — it also adds an option to
// that dropdown, silently when the member already has a label in the
// dashboard's AUDIENCE_TYPE_TO_LABEL (`country_code`, `landing_page_full_path`
// both do). Only add a dimension the funnel actually supports.
export const ConversionFunnelEntryPointList = [
  "device_type",
  "visitor_type",
  "source_channel",
  "source_site",
] as const;

export const ConversionFunnelEntryPointSchema = z.enum(
  ConversionFunnelEntryPointList
);

export type ConversionFunnelEntryPoint = z.infer<
  typeof ConversionFunnelEntryPointSchema
>;

export const AnalyticsOrderDistributionKindTypeList = [
  "histogram",
  "kde",
  "kde_cdf",
] as const;

export type AnalyticsOrderDistributionKindType =
  (typeof AnalyticsOrderDistributionKindTypeList)[number];

// NOTE: When this object is forwarded to the Python analytics service,
// IgAnalyticsClient.convertPayload's flattenObjectRemoveParents hoists every
// field here to the top level of the request body (snake_case), so Python's
// SitewideConversionSankeyRequestPayload reads them flat. Field names here
// MUST stay unique within the broader AnalyzeSitewideRequestBodySchema
// namespace — a top-level field (or another nested object) with the same
// name would silently shadow this one with no validation error.
export const ConversionFunnelSchema = z.object({
  // Stages
  abandonedCart: z.boolean().optional(),
  abandonedCheckout: z.boolean().optional(),
  addedToCart: z.boolean().optional(),
  bounced: z.boolean().optional(),
  converted: z.boolean().optional(),
  exitedBeforeAtc: z.boolean().optional(),
  startedCheckout: z.boolean().optional(),
  viewedCollectionPage: z.boolean().optional(),
  viewedProductPage: z.boolean().optional(),

  // Landing pages
  landingPageType: z.enum(LandingPageTypeList).optional(),
  secondPageType: z.enum(LandingPageTypeList).optional(),
});

export type ConversionFunnelType = z.infer<typeof ConversionFunnelSchema>;
